import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, AlertIcon, Button, FormControl, FormLabel, Heading, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Mutation, MutationAuthenticateSmsLoginCodeArgs, MutationPollSmsLoginBrowserArgs, MutationRequestSmsLoginLinkArgs } from '../../../generated/graphql';
import { AUTHENTICATE_SMS_LOGIN_CODE, POLL_SMS_LOGIN_BROWSER, REQUEST_SMS_LOGIN_LINK } from '../queries';
import { resumeEmailLinkDestination } from '../utils/emailLinkNavigation';
import { isCompleteSignInCode } from '../utils/signInCode';
import { SignInCodePairs } from './SignInCodePairs';
import { PhoneInputForm } from './PhoneInputForm';

/**
 * Reuse the module's country/phone controls for the tenant-selected SMS experience.
 *
 * Code first (2026-09-11): the SMS carries a sign-in link AND a four-digit code. Opening the
 * link signs in the device that opens it; typing the code here signs in THIS page. The
 * page keeps polling so a same-browser open still continues on its own. No browser
 * reference is shown — the code is the cross-device proof.
 */
export function SmsLinkSignIn({ onWaitingChange, ...props }: React.ComponentProps<typeof PhoneInputForm> & {
  /** Tells the host modal when the page is waiting for the code, so it can hide its first-step options and fit without a scroll. */
  onWaitingChange?: (waiting: boolean) => void;
}) {
  const apollo = useApolloClient();
  const returnPath = props.returnPath || '/';
  const [request, { loading, error }] = useMutation<Pick<Mutation, 'requestSmsLoginLink'>, MutationRequestSmsLoginLinkArgs>(REQUEST_SMS_LOGIN_LINK);
  const [verifyCode, { loading: verifying }] = useMutation<Pick<Mutation, 'authenticateSmsLoginCode'>, MutationAuthenticateSmsLoginCodeArgs>(AUTHENTICATE_SMS_LOGIN_CODE);
  const inFlight = useRef(false);
  const [pending, setPending] = useState<{ token: string; until: number; codeAvailable: boolean }>();
  useEffect(() => { onWaitingChange?.(!!pending); }, [pending, onWaitingChange]);
  useEffect(() => () => onWaitingChange?.(false), [onWaitingChange]);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [waitingError, setWaitingError] = useState('');
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(timer); }, []);
  useEffect(() => {
    if (!pending) return;
    const waiting = pending;
    let stopped = false;
    let timer: number;
    const navigate = resumeEmailLinkDestination;
    const onStorage = (event: StorageEvent) => { if (event.key === 'auth_token' && event.newValue) navigate(returnPath); };
    window.addEventListener('storage', onStorage);
    async function poll() {
      if (stopped) return;
      if (localStorage.getItem('auth_token')) { navigate(returnPath); return; }
      if (Date.now() >= waiting.until) { setWaitingError('This request has expired. Request a new SMS link.'); return; }
      try {
        const { data } = await apollo.mutate<Pick<Mutation, 'pollSmsLoginBrowser'>, MutationPollSmsLoginBrowserArgs>({ mutation: POLL_SMS_LOGIN_BROWSER, variables: { token: waiting.token } });
        if (stopped) return;
        if (data?.pollSmsLoginBrowser.auth?.token) {
          if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', data.pollSmsLoginBrowser.auth.token);
          navigate(data.pollSmsLoginBrowser.returnPath || returnPath);
          return;
        }
        if (data?.pollSmsLoginBrowser.status === 'EXPIRED') { setWaitingError('This request is no longer available. Request a new SMS link.'); return; }
        setWaitingError('');
      } catch { if (!stopped) setWaitingError('Unable to check sign-in yet. Retrying automatically…'); }
      if (!stopped) timer = window.setTimeout(poll, 3000);
    }
    timer = window.setTimeout(poll, 3000);
    return () => { stopped = true; window.clearTimeout(timer); window.removeEventListener('storage', onStorage); };
  }, [apollo, pending, returnPath]);
  async function send() {
    if (loading || retryAt > Date.now()) return;
    try {
      const { data } = await request({ variables: { input: { phoneNumber: props.phoneNumber, returnPath } } });
      const response = data?.requestSmsLoginLink;
      if (!response?.accepted) return;
      setPending({ token: response.browserToken, until: Date.now() + 600000, codeAvailable: !!response.codeAvailable });
      setRetryAt(Date.now() + response.retryAfterSeconds * 1000);
      setWaitingError('');
      setCode('');
      setCodeError('');
    } catch { /* The mutation error remains visible. */ }
  }
  // Submitted automatically when the fourth digit lands (SignInCodePairs passes the code in,
  // so this never reads a state value that has not re-rendered yet); the button is the fallback.
  async function verify(entered?: string) {
    const value = entered ?? code;
    if (!pending || verifying || inFlight.current || !isCompleteSignInCode(value)) return;
    inFlight.current = true;
    setCodeError('');
    try {
      const { data } = await verifyCode({ variables: { browserToken: pending.token, code: value } });
      const result = data?.authenticateSmsLoginCode;
      if (!result?.auth?.token) throw new Error('This code is incorrect or unavailable. Request a new SMS and try again.');
      if (!localStorage.getItem('auth_token')) localStorage.setItem('auth_token', result.auth.token);
      resumeEmailLinkDestination(result.returnPath || returnPath);
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : 'This code is incorrect or unavailable. Request a new SMS and try again.');
      setCode('');
    } finally {
      inFlight.current = false;
    }
  }
  if (!pending) return <PhoneInputForm {...props} sendLabel="Send sign-in link" loading={loading} error={error?.message || props.error} onSendCode={send} />;
  const cooldown = Math.max(0, Math.ceil((retryAt - now) / 1000));
  // Compact and centred on purpose: the whole waiting state must fit the login modal without a scroll.
  return <VStack align="center" spacing={3} textAlign="center" w="full">
    <Heading size="md">Check your messages</Heading>
    <Text fontSize="sm">{pending.codeAvailable
      ? `If sign-in is available for ${props.phoneNumber}, an SMS will arrive. Type the code from the message here, or open its link on this device.`
      : `If sign-in is available for ${props.phoneNumber}, an SMS link will arrive. Open it on this device to continue.`}</Text>
    {pending.codeAvailable && <FormControl as={VStack} spacing={2} align="center">
      <FormLabel htmlFor="sms-sign-in-code" m={0} fontSize="sm">Code from the SMS</FormLabel>
      <SignInCodePairs id="sms-sign-in-code" label="Code from the SMS" value={code} onChange={setCode} onComplete={entered => void verify(entered)} isDisabled={verifying} />
      <Tooltip label="Checks the code and signs in this page. It is checked automatically as soon as all four digits are in. Nothing is sent."><Button colorScheme="green" w="full" maxW="16rem" isDisabled={!isCompleteSignInCode(code)} isLoading={verifying} loadingText="Checking code…" onClick={() => void verify()}>Verify and continue</Button></Tooltip>
    </FormControl>}
    {(codeError || waitingError || error) && <Alert status="warning" fontSize="sm" borderRadius="md"><AlertIcon />{codeError || waitingError || error?.message}</Alert>}
    <HStack spacing={2} justify="center" flexWrap="wrap">
      <Tooltip label="Send a new ten-minute SMS. The previous link and code stop working."><Button size="sm" variant="outline" isLoading={loading} isDisabled={cooldown > 0} onClick={send}>{cooldown ? `Resend in ${cooldown}s` : 'Resend SMS'}</Button></Tooltip>
      <Tooltip label="Enter another number. This does not change the phone number saved on an account."><Button size="sm" variant="ghost" onClick={() => { setPending(undefined); setWaitingError(''); setCodeError(''); setCode(''); }}>Change mobile number</Button></Tooltip>
    </HStack>
    <Text fontSize="xs" color="gray.500" role="status">This request does not confirm delivery. Links and codes work once and expire in ten minutes.</Text>
  </VStack>;
}
